import dayjs, { Dayjs } from 'dayjs'

/**
 * Safely converts a dayjs object to ISO string with fallback
 * @param date - The dayjs object to convert
 * @param fallback - Optional fallback value (defaults to current time ISO string)
 * @returns ISO string representation of the date, or fallback if invalid
 */
export const safeToISOString = (date: Dayjs | null | undefined, fallback?: string): string => {
  if (!date) {
    return fallback || dayjs().toISOString()
  }

  try {
    // Check if the dayjs object is valid
    if (!date.isValid()) {
      return fallback || dayjs().toISOString()
    }

    const isoString = date.toISOString()
    // Double-check the result is valid
    if (!isoString || isoString === 'Invalid Date') {
      return fallback || dayjs().toISOString()
    }

    return isoString
  } catch (error) {
    console.error('Error in safeToISOString:', error)
    return fallback || dayjs().toISOString()
  }
}

/**
 * Safely formats a dayjs object with fallback
 * @param date - The dayjs object to format
 * @param format - The format string
 * @param fallback - Optional fallback value
 * @returns Formatted string representation of the date, or fallback if invalid
 */
export const safeFormat = (date: Dayjs | null | undefined, format: string, fallback?: string): string => {
  if (!date) {
    return fallback || ''
  }

  try {
    // Check if the dayjs object is valid
    if (!date.isValid()) {
      return fallback || ''
    }

    const formatted = date.format(format)
    // Double-check the result is valid
    if (!formatted || formatted === 'Invalid Date') {
      return fallback || ''
    }

    return formatted
  } catch (error) {
    return fallback || ''
  }
}

/**
 * Safely parses a value into a dayjs object
 * @param value - The value to parse (string, Date, dayjs, etc.)
 * @param fallback - Optional fallback dayjs object
 * @returns Valid dayjs object or fallback
 */
export const safeDayjs = (value: any, fallback?: Dayjs): Dayjs => {
  try {
    const parsed = dayjs(value)
    if (!parsed.isValid()) {
      return fallback || dayjs()
    }
    return parsed
  } catch (error) {
    return fallback || dayjs()
  }
}
